import { Injectable } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { filter, firstValueFrom } from 'rxjs';
import { User, UserStore } from '../../state/user.repository';
import { UserSettingsStore } from '../../state/user-settings.repository';
import { ActivityLogStore } from '../../state/activity-log.repository';

/**
 * Records raw join/leave/breakout-change events into `ActivityLogStore`.
 * The store is a flat per-event stream — roster aggregation happens at
 * read time in `aggregateAttendance` (see `activity-log.aggregate.ts`),
 * so this service has no merge or upsert logic.
 */
@Injectable({ providedIn: 'root' })
export class ActivityLogService {
  constructor(
    private _store: ActivityLogStore,
    private _users: UserStore,
    private _userSettings: UserSettingsStore
  ) {
    // Scoped to this instance: the user store is module-scoped, and a leaked
    // subscription would record every change once more per dead injector.
    this._users.breakoutRoomChanges$.pipe(takeUntilDestroyed()).subscribe((changes) => {
      for (const { user, previousBreakoutRoomId } of changes) {
        // Skip new users — their initial appearance isn't a room change.
        if (previousBreakoutRoomId === undefined) continue;
        this.recordBreakoutChange(user, previousBreakoutRoomId);
      }
    });
  }

  recordJoin(user: User): void {
    this._store.add({ ...this._baseFields(user), kind: 'joined', scope: 'room' });
  }

  recordLeft(user: User): void {
    this._store.add({ ...this._baseFields(user), kind: 'left', scope: 'room' });
  }

  /**
   * Backfills a 'joined' event for a peer that was already in the room when
   * we connected (phase-1 `onPeerJoined` snapshot). Uses the peer's
   * self-reported `joinedAt` as the event time (falling back to now), and a
   * deterministic id so replays across reconnects and page reloads are
   * idempotent.
   */
  recordPresence(user: User): void {
    // Dedup reads persisted state, so wait for IDB rehydration first.
    // Resolves immediately once `initialized$` has fired true.
    void firstValueFrom(this._store.initialized$.pipe(filter(Boolean))).then(() => {
      const joinedAt = user.data.joinedAt;
      const id = `presence|${this._userSettings.value.roomId}|${user.id}|${joinedAt ?? 'unknown'}`;
      if (this._store.has(id)) return;
      this._store.add({
        ...this._baseFields(user),
        id,
        time: joinedAt !== null ? new Date(joinedAt) : new Date(),
        kind: 'joined',
        scope: 'room',
      });
    });
  }

  recordBreakoutChange(user: User, previousBreakoutRoomId: string): void {
    const breakoutRoomId = user.data.breakoutRoomId ?? '';
    const joining = breakoutRoomId !== '';
    this._store.add({
      ...this._baseFields(user),
      kind: joining ? 'joined' : 'left',
      scope: 'breakout',
      breakoutRoom: joining ? breakoutRoomId : previousBreakoutRoomId,
    });
  }

  private _baseFields(user: User): {
    time: Date;
    roomId: string;
    userId: string;
    name: string;
    avatar: string;
  } {
    return {
      time: new Date(),
      roomId: this._userSettings.value.roomId,
      userId: user.id,
      name: user.data.name,
      avatar: user.data.avatar,
    };
  }
}
